import { useState, useContext } from "react";
import { Box, Typography, Button, Stack, TextField, Select, MenuItem, ListSubheader, FormControl, InputLabel } from "@mui/material";
import ResponsiveTopBar from "../components/ResponsiveTopBar";
import MobileNavDrawer from "../components/MobileNavDrawer";
import { DeviceContext } from "../context/DeviceContext";
import { PositionContext } from "../context/PositionContext";
import { useNavigate } from "react-router-dom";
import TickerSearch from "../components/TickerSearch";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiInputBase-input": { color: DL.textPrimary, fontSize: "0.9rem" },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.9rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: DL.bgLight,
    borderRadius: "8px",
    "& fieldset": { borderColor: DL.border },
    "&:hover fieldset": { borderColor: DL.borderSubtle },
    "&.Mui-focused fieldset": { borderColor: DL.green },
  },
  "& .MuiSelect-select": { color: DL.textPrimary, fontSize: "0.95rem", py: 1.4 },
  "& .MuiInputLabel-root": { color: DL.textMuted, fontSize: "0.95rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
  "& .MuiSvgIcon-root": { color: DL.textMuted },
};

const menuPaperSx = {
  PaperProps: {
    sx: {
      backgroundColor: DL.bgMedium,
      border: `1px solid ${DL.border}`,
      borderRadius: "10px",
      mt: 0.5,
      "& .MuiMenuItem-root": {
        fontSize: "0.95rem",
        color: DL.textPrimary,
        minHeight: 44,
        "&:hover": { backgroundColor: DL.bgLight },
        "&.Mui-selected": { backgroundColor: "rgba(98,244,129,0.1)", color: DL.green },
        "&.Mui-selected:hover": { backgroundColor: "rgba(98,244,129,0.15)" },
      },
      "& .MuiListSubheader-root": {
        backgroundColor: DL.bgDark,
        color: DL.textMuted,
        fontSize: "0.75rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        lineHeight: "32px",
      },
    },
  },
};

const defaultPosition = {
  type: "", ticker: "", entryPrice: "", quantity: "",
  strikePrice: "", lowerPutStrike: "", upperPutStrike: "",
  lowerCallStrike: "", upperCallStrike: "",
  stopLoss: "", takeProfit: "", expirationDate: "",
  optionType: "", premium: "", notes: "",
};

function CreatePosition() {
  const { isMobile } = useContext(DeviceContext);
  const { addPosition } = useContext(PositionContext);
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [position, setPosition] = useState(defaultPosition);
  const set = (field) => (e) => setPosition({ ...position, [field]: e.target.value });

  const handleSave = async () => {
    if (!position.type || !position.ticker) return;
    try {
      await addPosition(position);
      navigate(-1);
    } catch (error) {
      console.error("Error saving position:", error);
    }
  };

  const isOptions = position.type.includes("call") || position.type.includes("put");
  const hasQuantity =
    position.type.includes("crypto") ||
    position.type.includes("shares") ||
    position.type === "short-sell" ||
    isOptions;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.05) 0%, ${DL.bgDark} 55%)`,
        px: 2,
        py: isMobile ? 3 : 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
      <ResponsiveTopBar isMobile={isMobile} onMobileMenuToggle={() => setMobileMenuOpen((prev) => !prev)} />
      <MobileNavDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          mt: "64px",
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          boxShadow: "0 4px 40px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}>
        {/* Header */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3, borderBottom: `1px solid ${DL.divider}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: isMobile ? "1.3rem" : "1.6rem", color: DL.textPrimary }}>
            Create New Position
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: DL.textMuted, mt: 0.25 }}>
            Track a new trade or investment
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3 }}>
          <Stack spacing={2}>
            {/* Position type */}
            <FormControl fullWidth sx={selectSx}>
              <InputLabel>Position Type</InputLabel>
              <Select
                value={position.type}
                onChange={set("type")}
                label="Position Type"
                MenuProps={menuPaperSx}>
                <ListSubheader>Stock Positions</ListSubheader>
                <MenuItem value="buy-shares">Buy (Shares)</MenuItem>
                <MenuItem value="short-sell">Short Sell</MenuItem>
                <ListSubheader>Options Positions</ListSubheader>
                <MenuItem value="buy-call">Buy Call</MenuItem>
                <MenuItem value="sell-put">Sell Put</MenuItem>
                <MenuItem value="covered-call">Covered Call</MenuItem>
                <MenuItem value="iron-condor">Iron Condor</MenuItem>
                <MenuItem value="straddle">Straddle</MenuItem>
                <ListSubheader>Crypto Positions</ListSubheader>
                <MenuItem value="buy-crypto">Buy Crypto</MenuItem>
                <MenuItem value="sell-crypto">Sell Crypto</MenuItem>
              </Select>
            </FormControl>

            <TickerSearch
              ticker={position.ticker}
              setTicker={(ticker) => setPosition({ ...position, ticker })}
              label="Ticker Symbol"
              placeholder="Search for ticker (e.g., AAPL, BTC, SPY)"
            />

            <TextField
              label="Entry Price"
              variant="outlined"
              type="number"
              fullWidth
              value={position.entryPrice}
              onChange={set("entryPrice")}
              InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
              sx={inputSx}
            />

            <TextField
              label="Stop Loss (Optional)"
              variant="outlined"
              type="number"
              fullWidth
              value={position.stopLoss}
              onChange={set("stopLoss")}
              InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
              sx={inputSx}
            />

            <TextField
              label="Take Profit (Optional)"
              variant="outlined"
              type="number"
              fullWidth
              value={position.takeProfit}
              onChange={set("takeProfit")}
              InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
              sx={inputSx}
            />

            {position.type && (
              <>
                {hasQuantity && (
                  <TextField
                    label="Quantity"
                    variant="outlined"
                    type="number"
                    fullWidth
                    value={position.quantity}
                    onChange={set("quantity")}
                    sx={inputSx}
                  />
                )}

                {isOptions && (
                  <>
                    <TextField
                      label="Strike Price"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={position.strikePrice}
                      onChange={set("strikePrice")}
                      InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                      sx={inputSx}
                    />

                    <TextField
                      label="Expiration Date"
                      variant="outlined"
                      type="date"
                      fullWidth
                      value={position.expirationDate}
                      onChange={set("expirationDate")}
                      InputLabelProps={{ shrink: true }}
                      sx={inputSx}
                    />

                    <Box
                      component="select"
                      value={position.optionType}
                      onChange={set("optionType")}
                      sx={{ ...selectSx, color: position.optionType ? DL.textPrimary : DL.textMuted }}>
                      <option value="">Select Option Type</option>
                      <option value="AMERICAN">American</option>
                      <option value="EUROPEAN">European</option>
                    </Box>

                    <TextField
                      label="Premium (Optional)"
                      variant="outlined"
                      type="number"
                      fullWidth
                      value={position.premium}
                      onChange={set("premium")}
                      InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                      sx={inputSx}
                    />
                  </>
                )}

                {position.type === "iron-condor" && (
                  <>
                    {["lowerPutStrike", "upperPutStrike", "lowerCallStrike", "upperCallStrike"].map((field) => (
                      <TextField
                        key={field}
                        label={field.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())}
                        variant="outlined"
                        type="number"
                        fullWidth
                        value={position[field]}
                        onChange={set(field)}
                        InputProps={{ startAdornment: <Box component="span" sx={{ mr: 1, color: DL.textMuted }}>$</Box> }}
                        sx={inputSx}
                      />
                    ))}
                  </>
                )}
              </>
            )}

            <TextField
              label="Notes (Optional)"
              variant="outlined"
              fullWidth
              multiline
              rows={3}
              value={position.notes}
              onChange={set("notes")}
              placeholder="Add any additional notes about this position..."
              sx={inputSx}
            />
          </Stack>
        </Box>

        {/* Actions */}
        <Box sx={{ px: isMobile ? 3 : 4, py: 3, pt: 0, display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Button
            onClick={() => navigate(-1)}
            sx={{
              backgroundColor: DL.bgLight,
              border: `1px solid ${DL.textMuted}`,
              color: DL.textPrimary,
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              fontWeight: 600,
              fontSize: "0.875rem",
              textTransform: "none",
              "&:hover": { borderColor: DL.textSecondary, backgroundColor: "rgba(255,255,255,0.06)" },
              transition: "all 0.2s ease",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!position.type || !position.ticker}
            sx={{
              background: "linear-gradient(to right, #10b981, #059669)",
              color: "#fff",
              borderRadius: "8px",
              px: 3,
              py: 1,
              fontWeight: 700,
              fontSize: "0.875rem",
              textTransform: "none",
              boxShadow: "0px 2px 8px rgba(98,244,129,0.4)",
              "&:hover": { filter: "brightness(110%)" },
              "&:disabled": { opacity: 0.4 },
              transition: "all 0.2s ease",
            }}>
            Save Position
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default CreatePosition;
